import { useState, useEffect } from 'react';
import { updateApi } from '../utils/api';
import { ArrowUpCircle, X } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';

/**
 * Lightweight banner that shows when a software update is available.
 * Renders at the top of the page. Dismissible per session.
 * Only visible to admin users.
 */
export default function UpdateBanner() {
  const { user } = useAuthStore();
  const [updateInfo, setUpdateInfo] = useState<{ version: string; changelog: string | null } | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user || user.recovery_scope || (user.role !== 'admin' && !user.is_admin && !user.isAdmin)) return;

    // Check on mount, then every 30 minutes
    const check = async () => {
      try {
        const res = await updateApi.getStatus();
        const data = res.data;
        if (data.update_available && data.latest_version) {
          // Only show if not already dismissed for this version
          const dismissedVersion = sessionStorage.getItem('update_banner_dismissed');
          if (dismissedVersion !== data.latest_version) {
            setUpdateInfo({ version: data.latest_version, changelog: data.changelog });
          }
        } else {
          setUpdateInfo(null);
        }
      } catch {
        // Non-critical
      }
    };

    check();
    const interval = setInterval(check, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  if (!updateInfo || dismissed || !user || user.recovery_scope || (user.role !== 'admin' && !user.is_admin && !user.isAdmin)) return null;

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('update_banner_dismissed', updateInfo.version);
  };

  return (
    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-2 flex items-center justify-between text-sm z-50">
      <div className="flex items-center gap-2">
        <ArrowUpCircle className="w-4 h-4" />
        <span>
          <strong>Update verfügbar:</strong> Version {updateInfo.version}
          {updateInfo.changelog && <span className="opacity-95 ml-2">— {updateInfo.changelog.substring(0, 80)}{updateInfo.changelog.length > 80 ? '...' : ''}</span>}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <a href="/admin?tab=updates" className="underline hover:no-underline font-medium">
          Jetzt aktualisieren
        </a>
        <button onClick={handleDismiss} className="opacity-95 hover:opacity-100" aria-label="Update-Banner schließen">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
